import { Brand, createBranded } from '@deltasierra/type-utilities';
import { UploadId } from '../../../upload/Upload';
import { DeviceId } from '../../../devices/Device';
import { AssetIdAndLayerId, BuilderTemplateId } from '../../../builderTemplate/BuilderTemplate';
import { PublishedArtifactGroupId } from '../../../publishedArtifact/PublishedArtifactGroup';

export type BasicInstagramDetailsWithLinkedPage = {
    id: string;
    name: string;
    linkedPageId: string;
    linkedPageName: string;
};

export type UpdateInstagramConfigDto = {
    id: string;
    name: string;
};

export interface InstagramPublishData {
    builderTemplateId?: BuilderTemplateId;
    caption: string | undefined;
    deviceId: DeviceId | null;
    isStory?: boolean;
    linkedAssetLibraryAssetIds: AssetIdAndLayerId[];
    plannerId: number | undefined;
    publishedArtifactGroupId: PublishedArtifactGroupId | null;
    scheduledTime: Date | undefined;
    uploadIds: UploadId[];
}

/**
 * Represents an Instagram Media Container publishing status
 *
 * @see https://developers.facebook.com/docs/instagram-api/reference/ig-container#fields
 */
export enum InstagramContainerPublishingStatus {
    /**
     * The container was not published within 24 hours and has expired.
     */
    EXPIRED = 'EXPIRED',

    /**
     * The container failed to complete the publishing process.
     */
    ERROR = 'ERROR',

    /**
     * The container and its media object are ready to be published.
     */
    FINISHED = 'FINISHED',

    /**
     * The container is still in the publishing process.
     */
    IN_PROGRESS = 'IN_PROGRESS',

    /**
     * The container's media object has been published.
     */
    PUBLISHED = 'PUBLISHED',
}

export type InstagramContainerId = Brand<string, 'InstagramContainerId'>;
export const InstagramContainerId = createBranded<InstagramContainerId>();

/**
 * Represents an Instagram Media Container
 *
 * @see https://developers.facebook.com/docs/instagram-api/reference/ig-container
 */
export interface InstagramContainer {
    id: InstagramContainerId;

    status_code: InstagramContainerPublishingStatus;
}

export type InstagramUserId = Brand<string, 'InstagramUserId'>;
export const InstagramUserId = createBranded<InstagramUserId>();

/**
 * Represents an IG User's current content publishing usage.
 *
 * @see https://developers.facebook.com/docs/instagram-api/reference/ig-user/content_publishing_limit
 */
export interface InstagramContentPublishingLimit {
    config: {
        /**
         * The maximum number of Media the app user can publish within the quota_duration time period.
         */

        quota_total: number;
        /**
         * The period of time in seconds against which the quota_total is calculated (usually 86400 seconds, 24 hours)
         */

        quota_duration: number;
    };
    /**
     * The number of times the app user has published Media since the time specified in the since query string
     * parameter. If the since parameter is omitted, this value will be the number of times the app user has published
     * Media within the last `config.quota_duration` time period (usually usually 86400 seconds, 24 hours).
     */

    quota_usage: number;
}
